import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import useMediaDevices, {mediaErrorMessage} from '../../hooks/use-media-devices';
import { OpenTokContext } from '../opentok/opentok-context';

interface MediaContextProps {
    checkMediaDevices: (forceEnabled?: boolean) => Promise<void>;
    onDeviceChange: (changeVideoDevice: Function, changeAudioDevice: Function) => Promise<void>;
    isChecking: boolean;
    error: mediaErrorMessage | null;
}
interface Props {
	children: React.ReactNode;
}

export const MediaDevicesContext = createContext<MediaContextProps>({
    checkMediaDevices: () => Promise.resolve(), 
    onDeviceChange: () => Promise.resolve(), 
    isChecking: false, 
    error: null});

export const MediaProvider = (): MediaContextProps => {
    
    const [isChecking, setIsChecking] = useState(false);
    const { checkMediaDevices: checkMediaDevicesContext, onDeviceChange, error } = useMediaDevices();
    const { opentok } = useContext(OpenTokContext);

    const timeoutRef = useRef<NodeJS.Timeout>();
	const debounceDeviceChange = () => {
		// we're debouncing the devicechange event because it can fire multiple times in
		// quick succession if a device has multiple inputs/outputs
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = setTimeout(() => {
			onDeviceChange();
		}, 100);
	};

	const deviceChangeListener = useCallback(() => {
		debounceDeviceChange();
	}, [opentok]);
    
	useEffect(() => {
		// listen for device (camera/mic/speaker) changes, update list of devices on change
		try {
			navigator.mediaDevices.addEventListener('devicechange', deviceChangeListener);
		} catch (e) {
			// if the browser doesn't support this event, just ignore it
			console.error(e);
		}
		return () => navigator.mediaDevices.removeEventListener('devicechange', deviceChangeListener);
	}, [deviceChangeListener]);

    const checkMediaDevices = useCallback(async (forceEnabled = false) => {
        if (isChecking) return;
        setIsChecking(true);
        try {
            await checkMediaDevicesContext(forceEnabled);
        } catch (error) {
            console.error('Error checking media devices:', error);
        } finally {
            setIsChecking(false);
        }
    }, [isChecking]);

    return ({ 
        checkMediaDevices, 
        isChecking, 
        onDeviceChange, 
        error
    });
};

export const useMediaDevicesContext = () => {
    return useContext(MediaDevicesContext);
}

export const MediaDevicesProvider = ({ children }: Props) => {
    const media = MediaProvider();
    return (
        <MediaDevicesContext.Provider value={media}>
            {children}
        </MediaDevicesContext.Provider>
    );
}